import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { jstToday } from "../lib/jst";
import { api, errMsg } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { ProductGrid } from "../components/ProductGrid";
import { Cart } from "../components/Cart";
import { PaymentDialog } from "../components/PaymentDialog";
import { ReceiptDialog } from "../components/ReceiptDialog";
import { LowStockPanel } from "../components/LowStockPanel";
import { ShiftCloseButton } from "../components/ShiftCloseButton";

export default function Kasir() {
  const { t } = useI18n();
  const [products, setProducts] = useState([]);
  const [items, setItems] = useState([]);
  const [discount, setDiscount] = useState("");
  const [payOpen, setPayOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [tab, setTab] = useState("grocery");
  const [todayOnly, setTodayOnly] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/products");
    setProducts(data);
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const pick = (p) => {
    if (p.track_stock && p.stock <= 0) {
      toast.error(`${p.name} stoknya habis, tidak bisa dijual`);
      return;
    }
    setItems((prev) => {
      const found = prev.find((i) => i.product_id === p.id);
      if (found) {
        if (p.track_stock && found.qty + 1 > p.stock) {
          toast.error(`Stok ${p.name} hanya ${p.stock} ${p.unit}`);
          return prev;
        }
        return prev.map((i) =>
          i.product_id === p.id ? { ...i, qty: i.qty + 1 } : i
        );
      }
      return [
        ...prev,
        { product_id: p.id, name: p.name, price: p.price, qty: 1, unit: p.unit },
      ];
    });
  };

  const setQty = (id, qty) =>
    setItems((prev) =>
      qty <= 0
        ? prev.filter((i) => i.product_id !== id)
        : prev.map((i) => (i.product_id === id ? { ...i, qty } : i))
    );

  const scanBarcode = async (code, clear) => {
    const local = products.find((p) => p.barcode === code);
    if (local) {
      pick(local);
      clear();
      return;
    }
    try {
      const { data } = await api.get(`/products/barcode/${code}`);
      pick(data);
      clear();
      toast.success(`${data.name} ditambahkan`);
    } catch {
      const byName = products.filter((p) =>
        p.name.toLowerCase().includes(code.toLowerCase())
      );
      if (byName.length > 0) {
        pick(byName[0]);
        clear();
      } else {
        toast.error("Produk / barcode tidak ditemukan");
      }
    }
  };

  const subtotal = items.reduce((s, i) => s + i.price * i.qty, 0);
  const total = Math.max(subtotal - (Number(discount) || 0), 0);
  const today = jstToday();
  const todayCount = products.filter((p) => p.kind === "menu" && p.today_date === today).length;
  const list = useMemo(
    () =>
      products.filter(
        (p) =>
          p.kind === tab && (!(todayOnly && tab === "menu") || p.today_date === today)
      ),
    [products, tab, todayOnly, today]
  );

  const confirm = async (method, paidAmount) => {
    setBusy(true);
    try {
      const { data: order } = await api.post("/orders", {
        order_type: "retail",
        items,
        discount: Number(discount) || 0,
      });
      const { data: paid } = await api.post(`/orders/${order.id}/pay`, {
        payment_method: method,
        paid_amount: paidAmount || total,
      });
      setReceipt(paid);
      setItems([]);
      setDiscount("");
      setPayOpen(false);
      load();
      toast.success("Pembayaran berhasil");
    } catch (e) {
      toast.error(errMsg(e, "Gagal memproses transaksi"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col xl:flex-row min-h-screen">
      <div className="flex-1 p-6 lg:p-8">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="font-display text-3xl font-semibold mb-1">{t("kasir_title")}</h1>
            <p className="text-sm text-muted-foreground">{t("kasir_sub")}</p>
          </div>
          <ShiftCloseButton />
        </div>
        <div className="mb-6">
          <LowStockPanel />
        </div>
        <div className="flex flex-wrap gap-2 mb-4">
          {[
            ["grocery", t("tab_grocery")],
            ["menu", t("tab_menu")],
          ].map(([k, label]) => (
            <button
              key={k}
              data-testid={`kasir-tab-${k}`}
              onClick={() => setTab(k)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
                tab === k ? "bg-gray-900 text-white" : "bg-white border border-gray-200"
              }`}
            >
              {label}
            </button>
          ))}
          {tab === "menu" && todayCount > 0 && (
            <button
              data-testid="kasir-today-filter"
              onClick={() => setTodayOnly((v) => !v)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
                todayOnly ? "bg-amber-500 text-white" : "bg-white border border-gray-200"
              }`}
            >
              Menu Hari Ini · {todayCount}
            </button>
          )}
          <span className="text-xs text-muted-foreground self-center">
            {tab === "menu" ? "Menu resto & minuman" : "Barang toko"} · {list.length} item
          </span>
        </div>

        <ProductGrid products={list} onPick={pick} onBarcode={scanBarcode} />
      </div>
      <div className="xl:w-[380px] xl:sticky xl:top-0 xl:h-screen bg-white border-t xl:border-t-0 xl:border-l border-gray-200">
        <Cart
          items={items}
          onQty={setQty}
          onRemove={(id) => setQty(id, 0)}
          discount={discount}
          setDiscount={setDiscount}
          onSubmit={() => setPayOpen(true)}
          submitLabel={t("pay")}
          title={t("cart")}
          subtitle={t("cart_sub")}
          busy={busy}
        />
      </div>
      <PaymentDialog
        open={payOpen}
        onOpenChange={setPayOpen}
        total={total}
        onConfirm={confirm}
        busy={busy}
      />
      <ReceiptDialog order={receipt} onClose={() => setReceipt(null)} />
    </div>
  );
}
